// Copyright (c) Docugami, Inc. All rights reserved.

import styled from "styled-components";
import { dgFontFamily, userFontFamily } from "components/common/Fonts";
import { GroupedList, IGroup, IGroupHeaderProps, IGroupedList, SelectionMode, Shimmer, ShimmerElementType } from "@fluentui/react";
import { emptyGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { ICatalogItem } from "./Catalog";
import { useEffect, useRef } from "react";

const listGroupHeaderGrey = "#E8E8E8";

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    background: ${listGroupHeaderGrey};
    padding: 5px 10px;
    box-sizing: border-box;
    cursor: pointer;
`;

const HeaderText = styled.span`
    font-family: ${dgFontFamily};
    font-size: 12px;
    font-weight: 700;
    line-height: 24px;
    text-transform: uppercase;
`;

const ListRoot = styled.div`
    display: flex;
    overflow: auto;
    width: 100%;
`

interface CatalogItemsListProps {
    items: ICatalogItem[];
    groups: IGroup[];
    selectedItemId: string;
    onSelectItem: (id: string) => void;
    isLoading?: boolean;
}

export const CatalogItemsList = (props: CatalogItemsListProps): JSX.Element => {
    const listRef = useRef<IGroupedList>(null);

    useEffect(() => {
        listRef.current?.forceUpdate();
    }, [props.selectedItemId])

    function onRenderGroupHeader(props?: IGroupHeaderProps, defaultRender?: (props?: IGroupHeaderProps) => JSX.Element | null) {
        if (!props?.group) {
            return null;
        }

        return (
            <HeaderContainer onClick={() => props!.onToggleCollapse!(props!.group!)}>
                <HeaderText title={props.group.name}>
                    {props.group.name}
                </HeaderText>
            </HeaderContainer>
        )
    }

    function onRenderCell(nestingDepth?: number, item?: ICatalogItem, itemIndex?: number, group?: IGroup) {
        if (!item) {
            return null;
        }

        return <CatalogListItem selected={props.selectedItemId === item.id} item={item} onClick={() => props.onSelectItem(item.id)} />
    }

    if (!!props.isLoading) {
        return <ListLoading />
    }

    return (
        <ListRoot>
            <GroupedList
                componentRef={listRef}
                items={props.items}
                onRenderCell={onRenderCell}
                groups={props.groups}
                selectionMode={SelectionMode.single}
                styles={{
                    root: {
                        width: "100%",
                        ".ms-List-cell": {
                            "min-height": "unset"
                        }
                    }
                }}
                groupProps={{
                    headerProps: {
                        styles: {
                            headerCount: {
                                display: 'none'
                            }
                        }
                    },
                    onRenderHeader: onRenderGroupHeader,
                }} />
        </ListRoot>
    )
}

const ListItemContainer = styled.div<{ selected?: boolean }>`
    display: flex;
    flex-direction: row;
    min-height: 45px;
    padding: 7px 10px;
    gap: 2px;
    box-sizing: border-box;
    cursor: pointer;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid ${emptyGray};
    background-color: ${props => !!props.selected ? primaryTheme.palette.neutralLighter : primaryTheme.palette.white};

    :hover {
        ${props => !props.selected && `background-color: ${primaryTheme.palette.neutralLighterAlt}`};
        :active {
            background-color: ${primaryTheme.palette.neutralLight}
        }
    }
`;

const ListItemText = styled.span`
    font-family: ${userFontFamily};
    font-size: 16px;
    font-weight: 400;
    line-height: 18px;
`;

interface CatalogListItemProps {
    item: ICatalogItem;
    selected?: boolean;
    onClick: () => void;
}

const CatalogListItem = (props: CatalogListItemProps): JSX.Element => {
    const { id, name, primaryAction } = props.item;

    return (
        <ListItemContainer key={id} data-key={name} onClick={props.onClick} selected={props.selected}>
            <ListItemText>{name}</ListItemText>
            {primaryAction}
        </ListItemContainer>
    )
}

// Skeleton loader for catalog list
const ListShimmerSkeletonContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ShimmerItemContainer = styled.div`
    background-color: ${primaryTheme.palette.white};
    height: 45px;
    border-bottom: 1px solid ${emptyGray};
`;

const ListLoading = (): JSX.Element => {
    return (
        <ListShimmerSkeletonContainer>
            {Array(12).fill("").map((x, index) => (
                <ShimmerItemContainer key={`shimmer_${index}`}>
                    <Shimmer isDataLoaded={false} width="85%" shimmerElements={[{ type: ShimmerElementType.line, height: 14 }]} style={{ margin: "7px 10px 3px" }} />
                    <Shimmer isDataLoaded={false} width="90%" shimmerElements={[{ type: ShimmerElementType.line, height: 14 }]} style={{ marginLeft: "10px" }} />
                </ShimmerItemContainer>
            ))}
        </ListShimmerSkeletonContainer>
    )
}